import { useEffect, useMemo, useState } from "react";
import { browserHistory } from "react-router";
import _ from "underscore";
import { compose } from "redux";

import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";

import states from "admin/routes.states";

import { accountingConnector } from "../../../services/accounting/connector";

import InvestorDepositContent from "./accountConfirm/InvestorDepositContent";
import InvoicesContent from "./accountConfirm/InvoicesContent";
import DepositsContent from "./accountConfirm/DepositsContent";
import InfoBeforeConfirm from "./accountConfirm/InfoBeforeConfirm";
import InfoAfterConfirm from "./accountConfirm/InfoAfterConfirm";
import AmlFlaggedDepositsContent from "./accountConfirm/AmlFlaggedDepositsContent";
import NonTransferableDepositsContent from "./accountConfirm/NonTransferableDepositsContent";

const AccountingConfirmComponent = ({
  accounting,
  initialSelectedIndex,
  transferInvoicePayment,
}) => {
  const [confirmDateDeposit, setConfirmDateDeposit] = useState(new Date());
  const [confirmDateInvoice, setConfirmDateInvoice] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toSendList, setToSendList] = useState([]);
  const [sendingInvoices, setSendingInvoices] = useState(false);
  const [toSendIndex] = useState();
  const [tabValue, setTabValue] = useState(initialSelectedIndex);
  const [itemsToExclude, setItemsToExclude] = useState([]);

  useEffect(() => {
    if (accounting.investorDepositsToTransfer.data.length !== 0)
      tryToggleOnDataEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accounting.investorDepositsToTransfer.data.length > 0)
      tryToggleOnDataEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounting.investorDepositsToTransfer.data.length]);

  const [
    matchedDeposits,
    sumToTransferDeposits,
    sumToClientTransferInvoices,
    sumToSavelendTransferInvoices,
    sumFromSavelendToClientTransferInvoices,
    invoiceMatched,
  ] = useMemo(() => {
    const matchedDeposits = _.filter(
      accounting.depositPayments.data,
      (item) => {
        return !!accounting.matchedDeposits.data[item.Id];
      },
    );

    const sumToTransferDeposits = _.reduce(
      matchedDeposits,
      (memo, num) => {
        if (accounting.matchedDeposits.data[num.Id].RemoveFromSending)
          return memo + 0;
        else return memo + num.Amount.Amount;
      },
      0,
    );

    const sumToClientTransferInvoices = _.reduce(
      accounting.matchedInvoices.data,
      (memo, num) => {
        if (num.RemoveFromSending) return memo + 0;
        else return memo + num.ForClientAccount.Amount;
      },
      0,
    );

    const sumToSavelendTransferInvoices = _.reduce(
      accounting.matchedInvoices.data,
      (memo, num) => {
        if (num.RemoveFromSending) return memo + 0;
        else return memo + num.ForSavelendAccount.Amount;
      },
      0,
    );

    const sumFromSavelendToClientTransferInvoices = _.reduce(
      accounting.matchedInvoices.data,
      (memo, num) => {
        if (num.RemoveFromSending) return memo + 0;
        else return memo + num.FromSavelendAccountToClientAccount.Amount;
      },
      0,
    );

    const invoiceMatched = _.sortBy(
      accounting.matchedInvoices.data,
      (invoice) => {
        return new Date(invoice.DateEntered).valueOf();
      },
    ).reverse();
    return [
      matchedDeposits,
      sumToTransferDeposits,
      sumToClientTransferInvoices,
      sumToSavelendTransferInvoices,
      sumFromSavelendToClientTransferInvoices,
      invoiceMatched,
    ];
  }, [accounting]);

  let actions = [
    <Button
      key="close"
      variant="contained"
      color="primary"
      onClick={closeDialog}
      style={{ float: "left" }}
    >
      Close
    </Button>,
    <Button
      key="sendInvoices"
      variant="contained"
      color="primary"
      disabled={sendingInvoices}
      onClick={transferInvoices}
    >
      Send Invoices
    </Button>,
  ];

  return (
    <div className="accounting-layout body-height">
      <Dialog open={dialogOpen}>
        <DialogContent>
          {sendingInvoices ? (
            <InfoAfterConfirm {...{ toSendIndex, toSendList }} />
          ) : (
            <InfoBeforeConfirm length={toSendList.length} />
          )}
        </DialogContent>
        <DialogActions>{actions}</DialogActions>
      </Dialog>
      <Tabs value={tabValue} onChange={changeTab}>
        <Tab label="Investor deposits" />
        <Tab label="Non-Transferable deposits" />
        <Tab label="AML flagged deposits" />
        <Tab label="Invoices" />
        <Tab label="Deposits" />
      </Tabs>
      {tabValue === 0 && (
        <InvestorDepositContent
          {...{
            confirmDateDeposit,
            idsToExcludeFromTransfer,
            setConfirmDateDeposit,
            toggleMatchedInvestorDepositSendingFlag,
            itemsToExclude,
          }}
        />
      )}
      {tabValue === 1 && (
        <NonTransferableDepositsContent
          {...{
            idsToExcludeFromTransfer,
            toggleMatchedInvestorDepositSendingFlag,
            itemsToExclude,
          }}
        />
      )}
      {tabValue === 2 && (
        <AmlFlaggedDepositsContent
          {...{
            idsToExcludeFromTransfer,
            toggleMatchedInvestorDepositSendingFlag,
            itemsToExclude,
          }}
        />
      )}
      {tabValue === 3 && (
        <InvoicesContent
          {...{
            invoiceMatched,
            sumToSavelendTransferInvoices,
            sumToClientTransferInvoices,
            sumFromSavelendToClientTransferInvoices,
            confirmDateInvoice,
            setConfirmDateInvoice,
            openConfirmation,
          }}
        />
      )}
      {tabValue === 3 && (
        <DepositsContent
          {...{
            matchedDeposits,
            sumToTransferDeposits,
            confirmDateDeposit,
            setConfirmDateDeposit,
          }}
        />
      )}
    </div>
  );

  function tryToggleOnDataEntry() {
    const investorDepositsWithoutDepotIdOrWithAmlWarning = _.filter(
      accounting.investorDepositsToTransfer.data,
      (item) => {
        return !item.DepotId;
      },
    );

    for (let investorDeposit of investorDepositsWithoutDepotIdOrWithAmlWarning) {
      toggleMatchedInvestorDepositSendingFlag(investorDeposit);
    }
  }

  function toggleMatchedInvestorDepositSendingFlag(item) {
    let list = [...itemsToExclude];

    if (idsToExcludeFromTransfer(item)) {
      // If it's on the exclude list but depotId is not yet set we cannot remove it from exclusion
      if (!item.DepotId) return;

      list = _.filter(list, (idx) => {
        return idx !== item.InvestorDepositId;
      });
    } else list.push(item.InvestorDepositId);
    setItemsToExclude(list);
  }

  function redirectToLocation(tab) {
    browserHistory.push(states[tab].url);
  }

  function openConfirmation() {
    let data = accounting.matchedInvoices.data;
    let toSendList = _.filter(data, (item) => {
      return item.RemoveFromSending !== true;
    });
    setDialogOpen(true);
    setToSendList(toSendList || []);
  }

  function closeDialog() {
    setDialogOpen(false);
    setSendingInvoices(false);
  }

  function changeTab(event, newValue) {
    setTabValue(newValue);
    if (newValue === 0) redirectToLocation("confirmInvestorDeposits");
    if (newValue === 1) redirectToLocation("confirmPaymentsInvoices");
    if (newValue === 2) redirectToLocation("confirmPaymentsDeposits");
  }

  function idsToExcludeFromTransfer(item) {
    return (
      !item.DepotId ||
      _.some(itemsToExclude, (value) => {
        return item.InvestorDepositId === value;
      })
    );
  }

  function transferInvoices() {
    transferInvoicePayment(
      accounting.matchedInvoices?.data,
      confirmDateInvoice,
    );
    setDialogOpen(false);
  }
};

export default compose(accountingConnector)(AccountingConfirmComponent);
